/**
 * 単体送付状と束ね（#223、docs/89 §5.3・§6）：帳票に紐づかない送付状（図面・カタログなどを FAX・封筒で送る1枚目）に、
 * 2回目から「書類追加」で同じ送付先の出した帳票（{project}をまたぐ）・受け取った書類・その場で選ぶ PDF を同封できる＝束ねの画面。
 *   /cover-letters/new?partner=<id>[&branch=<id>][&group=<id>][&items=<鍵>,<鍵>…]
 *     鍵は contract の coverItemKey（quote.<id>・quote.<id>.flat・invoice.<id>・received.<id>・file.<id> …）。並びは URL の順＝記の行と PDF の順
 *   入口：{customer}・{vendor}の詳細と{project}の画面の「⋮」→「送付状作成」（左のメニューに足さない＝§15 の12）、
 *         帳票の紙の画面の送付状の枠の「同じ送付先の書類を追加」（選んだ書類とその帳票を持って来る。枠で直した中身も引き継ぐ＝location.state）
 *   送付先（{customer}／{vendor}・支店）を選ぶ → 送付状の枠（スイッチなし＝いつも送付状。窓付封筒のスイッチはいつも出す）→ 見本と同封書類の紙 →
 *   「印刷」「PDF 保存」（送付状＋同封書類の PDF 1本を作って送付の記録に固める）
 *   送付先を持つ書類（帳票・見積依頼書）が入っているあいだは送付先を変えられない（同じ送付先だけを束ねる）。PC だけ（contract の PC_ONLY_FEATURES）
 * 3回目の前の直し（2026-10-21 射場さんの決め、docs/89 ★12〜★14・§17.3・§17.4）：
 *   ・入口に左のメニュー「送付状」の「送付状作成」と、{project}のタブ「送付」の「送付状作成」（partner＝{project}の{customer}・branch・group）
 *   ・送付先は「{customer}／{vendor}／未登録」（?to=customer|vendor|new。省けば partner の種別から）。未登録は送付状の枠の送付先の欄に 会社名・部署・担当者・〒・住所
 *     （FAX は送信先 FAX）と「{customer}に登録」。未登録の「書類追加」は PDF ファイルだけ（帳票は同封できない）。
 *     「{customer}に登録」がオンなら、印刷・PDF の保存の瞬間に{customer}を登録（POST /partners）してから、登録した{customer}あてに送付する
 * {order}の約款の別紙（#233 2c）：同封書類の{order}ごとにスイッチ「約款」（鍵の order.<id>.terms／.noterms。省けば会社の「印刷で約款」）
 */
import { COVER_LETTER_MSG as M, ORDER_MSG, coverItemKey, isAddressedItem, parseCoverItemKey, type DeliveryItemRef, type Partner, type PartnerList } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router";
import { api, messageOf } from "../api.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { CoverBundlePicker, useItemPages } from "../components/CoverBundle.jsx";
import { CoverLetterPanel, contentOf, useCoverLetter, type CoverForm } from "../components/CoverLetterPanel.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { ErrorText, useToast } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../lib/pdfPages.js";

/** 束ねの画面に持って来る中身（帳票の紙の画面の「同じ送付先の書類を追加」） */
export type CoverBundleState = { cover?: { form: CoverForm; on: boolean }; labels?: Record<string, string> };

/** 書類名の欄は打ち終わってから送る（見本と記の行を打つたびに作り直さない） */
function useDebounced<T>(v: T, ms: number): [T, (v: T) => void] {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = window.setTimeout(() => setD(v), ms);
    return () => window.clearTimeout(id);
  }, [v, ms]);
  return [d, setD];
}

export function CoverLetterNewPage() {
  const [sp, setSp] = useSearchParams();
  const loc = useLocation();
  const carried = (loc.state ?? null) as CoverBundleState | null;
  const partner = sp.get("partner");
  const branch = sp.get("branch");
  const group = sp.get("group");
  const to = sp.get("to");
  const L = useLabels();
  const qc = useQueryClient();
  const { toast, node } = useToast();
  // 戻り先（直前の画面が無いとき）：{project}・{customer}の画面、どちらも無ければ左のメニューの「送付状」（★12）
  const back = useBackNav(group ? { to: `/groups/${group}`, label: L.project } : partner ? { to: `/partners/${partner}`, label: L.customer } : { to: "/cover-letters", label: M.listTitle });
  usePageName(M.newTitle);
  const list = useQuery({ queryKey: ["partners-all"], queryFn: () => api<PartnerList>("GET", "/partners?type=all") });
  /** 送付先の選び方：{customer}／{vendor}／未登録（★13）。?to が無ければ選んだ取引先の種別から */
  const row = list.data?.items.find((p) => p.id === partner);
  const kind: "customer" | "vendor" | "new" = to === "new" ? "new" : to === "vendor" ? "vendor" : to === "customer" ? "customer" : row?.partnerType === "vendor" ? "vendor" : "customer";
  const unregistered = kind === "new";
  const options = (list.data?.items ?? []).filter((p) => (kind === "vendor" ? p.partnerType !== "client" : p.partnerType !== "vendor"));
  /** 同封書類（URL の items= の順） */
  const refs = useMemo(() => (sp.get("items") ?? "").split(",").filter(Boolean).map(parseCoverItemKey).filter((r): r is DeliveryItemRef => !!r), [sp]);
  const [labels, setLabels] = useState<Record<string, string>>(carried?.labels ?? {});
  const [sentLabels, setSentLabels] = useDebounced(labels, 600);
  const items = useMemo(
    () => refs.map((r): DeliveryItemRef => ((r.kind === "received" || r.kind === "file") && sentLabels[coverItemKey(r)]?.trim() ? { ...r, label: sentLabels[coverItemKey(r)]!.trim() } : r)),
    [refs, sentLabels],
  );
  const target = unregistered ? { items, unregistered: true, ...(group ? { groupId: group } : {}) } : partner ? { items, partnerId: partner, branchId: branch, ...(group ? { groupId: group } : {}) } : null;
  const cover = useCoverLetter(target, { switchable: false, initial: carried?.cover ?? null });
  const itemPages = useItemPages(refs);
  const addressed = refs.some(isAddressedItem);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  useEffect(() => {
    if (!printPages) return;
    const f = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(f);
  }, [printPages]);

  /** URL の値を変える（送付先・同封書類の並び）。履歴は積まない */
  const setParams = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(p)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true, state: loc.state });
  };
  const setItems = (rs: DeliveryItemRef[]) => setParams({ items: rs.map(coverItemKey).join(",") || null });
  const bundle = {
    labels,
    onLabel: (key: string, v: string) => setLabels((ls) => ({ ...ls, [key]: v })),
    onMove: (i: number, dir: -1 | 1) => {
      const j = i + dir;
      if (j < 0 || j >= refs.length) return;
      const next = refs.slice();
      [next[i], next[j]] = [next[j]!, next[i]!];
      setItems(next);
    },
    onRemove: (i: number) => setItems(refs.filter((_, j) => j !== i)),
  };
  // {order}の約款の別紙（2c）：別紙にできる{order}（下書きの答えの terms が null でない）ごとにスイッチ。初めは会社の「印刷で約款」（答えの値）
  const draftItems = cover.draft.data?.items ?? [];
  const termOrders = refs.flatMap((r) => {
    if (r.kind !== "order") return [];
    const it = draftItems.find((x) => x.kind === "order" && x.key.split(".")[1] === r.id);
    return it && it.terms !== null ? [{ r, it }] : [];
  });
  const termsControls = termOrders.length
    ? {
        rows: termOrders.map(({ r, it }) => ({ id: r.id, label: termOrders.length > 1 && it.docNo ? `${ORDER_MSG.termsPrint}（${it.docNo}）` : ORDER_MSG.termsPrint, on: (r.kind === "order" ? r.terms : undefined) ?? !!it.terms })),
        onChange: (oid: string, v: boolean) => setItems(refs.map((r) => (r.kind === "order" && r.id === oid ? { ...r, terms: v } : r))),
      }
    : undefined;

  const go = async (method: "print" | "download") => {
    setBusy(true);
    try {
      let over: Parameters<typeof cover.deliver>[1];
      const f = cover.form;
      // 未登録の送付先を{customer}に登録（★13）：先に{customer}を登録し、登録した{customer}あてに送付する（同じ名前があれば登録の断りで止まる＝送付しない）
      if (unregistered && f?.registerPartner) {
        const r = f.recipient;
        const created = await api<Partner>("POST", "/partners", {
          name: r.name,
          partnerType: "client",
          ...(r.contact.trim() ? { contactName: r.contact } : {}),
          ...(r.department.trim() ? { contactDepartment: r.department } : {}),
          ...(r.postalCode.trim() ? { postalCode: r.postalCode } : {}),
          ...(r.address.trim() ? { address: r.address } : {}),
          ...(f.toFax.trim() ? { fax: f.toFax } : {}),
        });
        void qc.invalidateQueries({ queryKey: ["partners"] });
        void qc.invalidateQueries({ queryKey: ["partners-all"] });
        over = { target: { items, partnerId: created.id, ...(group ? { groupId: group } : {}) }, cover: { ...contentOf(f), contactId: created.contacts[0]?.id ?? null } };
        // この画面はその{customer}あての送付状に（もう一度押しても2回登録しない）
        setParams({ to: null, partner: created.id, branch: null });
        toast(t(M.registered).replace("{name}", created.name));
      }
      const { result, blob } = await cover.deliver(method, over);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      if (method === "download") downloadBlob(blob, result.fileName);
      else {
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        setPrintPages(await portraitForPrint(got));
      }
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const d = cover.draft.data;
  const needName = unregistered && !cover.form?.recipient.name.trim();
  const canOut = cover.ready && !busy && !cover.bundleError && sentLabels === labels && !needName;
  return (
    <div className="paperview" data-testid="cover-new-page">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{M.newTitle}</h1>
        {/* 送付先：{customer}／{vendor}／未登録（★13）。送付先を持つ書類（帳票・見積依頼書）が入っているあいだは変えられない */}
        <span className="cllbl" id="cover-new-kind-l">
          {M.recipientKind}
        </span>
        <Segmented
          options={[
            { value: "customer" as const, label: L.customer, testId: "cover-new-kind-customer", disabled: addressed && kind !== "customer" },
            { value: "vendor" as const, label: L.vendor, testId: "cover-new-kind-vendor", disabled: addressed && kind !== "vendor" },
            { value: "new" as const, label: M.kindUnregistered, testId: "cover-new-kind-new", disabled: addressed },
          ]}
          value={kind}
          onChange={(k) => setParams({ to: k, partner: null, branch: null })}
          labelledBy="cover-new-kind-l"
          testId="cover-new-kind"
        />
        {unregistered ? null : (
          <select
            className="inp"
            style={{ maxWidth: 320 }}
            aria-label={kind === "vendor" ? L.vendor : L.customer}
            value={partner ?? ""}
            disabled={addressed}
            title={addressed ? M.recipientMixed : undefined}
            onChange={(e) => setParams({ partner: e.target.value || null, branch: null })}
            data-testid="cover-new-partner"
          >
            <option value="">{M.pickPartner}</option>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </select>
        )}
        {/* 支店・営業所（その{customer}に支店があるときだけ）。送付先を持つ書類が入っているあいだは変えられない */}
        {!unregistered && d?.branches.length ? (
          <select className="inp" style={{ maxWidth: 220 }} aria-label={M.branch} value={branch ?? ""} disabled={addressed} onChange={(e) => setParams({ branch: e.target.value || null })} data-testid="cover-new-branch">
            <option value="">{M.branchNone}</option>
            {d.branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        ) : null}
        <span className="sp" />
        <button type="button" className="btn" disabled={!canOut} onClick={() => void go("download")} data-testid="cover-new-save">
          {M.save}
        </button>
        <button type="button" className="btn primary" disabled={!canOut} onClick={() => void go("print")} data-testid="cover-new-print">
          {busy ? M.making : M.print}
        </button>
      </div>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      {!partner && !unregistered ? <p className="hint">{t(M.partnerRequired)}</p> : null}
      {partner || unregistered ? (
        <div className="paperwrap">
          <div className="pages" aria-label={M.newTitle}>
            {cover.pages.map((p, i) => (
              <div className="clsheet" key={i} data-testid="cover-sheet">
                <img src={p.url} alt={`${M.newTitle} ${i + 1}ページ目`} width={p.w} height={p.h} />
              </div>
            ))}
            {!cover.pages.length && !cover.previewErr ? <p className="hint" role="status">{M.making}</p> : null}
            {/* 同封書類の紙（並びの順）。束ねで入るのと同じ PDF を見るだけの粗さで */}
            {refs.map((r, i) => {
              const q = itemPages[i];
              const it = d?.items.find((x) => x.key === coverItemKey(r));
              const name = it ? `${it.label}${it.docNo ? ` ${it.docNo}` : ""}` : M.bundleTitle;
              return (
                <div className="clitem" key={coverItemKey(r)} data-testid="cover-item-pages" data-key={coverItemKey(r)}>
                  {q?.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
                  {q?.data
                    ? q.data.map((p, j) => (
                        <div className="clsheet clitempage" key={j} style={p.w > p.h ? { width: "min(100%, 1160px)" } : undefined}>
                          <img src={p.url} alt={`${name} ${j + 1}ページ目`} width={p.w} height={p.h} />
                        </div>
                      ))
                    : !q?.isError
                      ? <p className="hint" role="status">{`${name}　${M.loading}`}</p>
                      : null}
                </div>
              );
            })}
          </div>
          <CoverLetterPanel c={cover} switchable={false} collapsible={false} bundle={refs.length ? bundle : undefined} onAddDocs={() => setPicking(true)} terms={termsControls} />
        </div>
      ) : null}
      {picking && target ? (
        <CoverBundlePicker
          target={target}
          fileOnly={unregistered}
          onClose={() => setPicking(false)}
          onPick={(picked, ls) => {
            setPicking(false);
            if (Object.keys(ls).length) {
              const merged = { ...labels, ...ls };
              setLabels(merged);
              setSentLabels(merged);
            }
            setItems([...refs, ...picked]);
          }}
        />
      ) : null}
      {back.dialog}
      {node}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(printPages ?? cover.pages).map((p, i) => (
            <div className="printedPage" key={i}>
              <img src={p.url} alt="" width={p.w} height={p.h} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
